import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import {
  type UpdateDeliveryInput,
  useDeliveryApi,
  useSendDeliveryApi,
  useUpdateDeliveryApi,
} from "hooks/use-delivery-api";
import { useSenderEmails } from "hooks/use-sender-emails-api";
import { DeliveryTiming } from "hooks/useEmailTemplatesRestApi";
import type { FC } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  DeliveryForm,
  type DeliveryFormValues,
} from "routes/DeliveryPage/components/DeliveryForm";
import {
  fromDateTimeLocalValue,
  toDateTimeLocalValue,
} from "utils/relative-time";

export const DeliveryEditPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: delivery,
    loading: deliveryLoading,
    error: deliveryError,
  } = useDeliveryApi(id || "");
  const { updateDelivery, loading: updateLoading } = useUpdateDeliveryApi();
  const { sendDelivery, loading: sendLoading } = useSendDeliveryApi();
  const { data: senderEmailsData } = useSenderEmails({ verifiedOnly: true });

  if (deliveryLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center py-12">
          <Loading size="lg" />
        </div>
      </div>
    );
  }

  if (deliveryError || !delivery) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="mb-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/deliveries">&larr; 戻る</Link>
          </Button>
        </div>
        <div className="alert alert-error">
          <span>{deliveryError?.message || "配信メールが見つかりません"}</span>
        </div>
      </div>
    );
  }

  if (delivery.status !== "draft" && delivery.status !== "scheduled") {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="mb-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/deliveries">&larr; 戻る</Link>
          </Button>
        </div>
        <div className="alert alert-warning">
          <span>この配信は編集できません（ステータス: {delivery.status}）</span>
        </div>
      </div>
    );
  }

  const scheduledAt = toDateTimeLocalValue(delivery.scheduledAt);

  const initialValues: DeliveryFormValues = {
    distributionGroupId: delivery.groupId || "",
    includeSender: delivery.includeSender,
    from: delivery.fromEmail,
    subject: delivery.subject,
    body: delivery.bodyText,
    deliveryTiming: delivery.scheduledAt
      ? DeliveryTiming.Scheduled
      : DeliveryTiming.Now,
    scheduledAt,
  };

  const buildUpdateInput = (
    values: DeliveryFormValues,
  ): UpdateDeliveryInput => {
    const senders = senderEmailsData?.items ?? [];
    const sender = senders.find(s => s.email === values.from);
    return {
      groupId: values.distributionGroupId,
      fromEmail: values.from,
      fromName: sender?.displayName || undefined,
      subject: values.subject.trim(),
      bodyText: values.body.trim(),
      includeSender: values.includeSender,
      scheduledAt:
        values.deliveryTiming === DeliveryTiming.Scheduled
          ? fromDateTimeLocalValue(values.scheduledAt)
          : null,
    };
  };

  const handleSave = async (values: DeliveryFormValues) => {
    if (!id) return;
    await updateDelivery(id, buildUpdateInput(values));
    navigate("/deliveries");
  };

  const handleSend = async (values: DeliveryFormValues) => {
    if (!id) return;
    await updateDelivery(id, buildUpdateInput(values));
    if (values.deliveryTiming === DeliveryTiming.Now) {
      await sendDelivery(id);
    }
    navigate("/deliveries");
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="flex items-center gap-4 mb-6">
        <Button asChild variant="ghost" size="sm">
          <Link to="/deliveries">&larr; 戻る</Link>
        </Button>
        <PageTitle>配信メール編集</PageTitle>
      </div>

      <DeliveryForm
        initialValues={initialValues}
        saveDraftLabel="保存"
        saveLoading={updateLoading}
        sendLoading={sendLoading}
        onSaveDraft={handleSave}
        onSend={handleSend}
      />
    </div>
  );
};
